import React, { useEffect, useRef, useState } from "react";
import {
    Box,
    CircularProgress,
    Divider,
    Grid,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from "@mui/material";
import { useParams, useSearchParams } from "react-router-dom";
import { useReactToPrint } from "react-to-print";
import WSButton from "../../Components/Button/WSButton";
import {
    callAPI,
    convertToUTCISOString,
    dateFormat,
    formatIndianCurrency,
    getLocalValue,
    LoacalVariables,
    Method,
} from "../../commonFunctions/CommonFunctions";
import { useSnackbar } from "../../Components/Snackbar/SnackBarContext";
import { NotFound } from "../NotFound";
import logo from "../../images/logo.svg";
import { IBranch } from "../DTO/Dto";
import http from "../../commonFunctions/httpClient";

const CustomerStatementPrint: React.FC = () => {
    const { id } = useParams();
    const contentRef = useRef<HTMLDivElement>(null);
    const { showMessage } = useSnackbar();
    const [data, setData] = useState<any>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [branchData, setBranchData] = useState<IBranch>();
    const [searchParams] = useSearchParams();

    const from: any = searchParams.get("from");
    const to: any = searchParams.get("to");

    useEffect(() => {
        async function getDataById() {
            try {
                const fromDate = convertToUTCISOString(from)
                const toDate = convertToUTCISOString(to)
                const response: any = await http.get(`JournelEntry/lead/${id}?from=${fromDate}&to=${toDate}`);
                if (response) setData(response);
            } catch (error) {
                showMessage("Something went wrong. Please try again.", "error");
            } finally {
                setLoading(false);
            }
        }


        async function getBranchDetails() {
            try {
                const response: any = await callAPI(
                    "Branch",
                    Method.GET,
                    null,
                    getLocalValue(LoacalVariables.Branch)
                );
                if (response?.data) setBranchData(response.data);
            } catch (error) {
                showMessage("Something went wrong. Please try again.", "error");
            }
        }

        if (id) {
            getDataById();
            getBranchDetails();
        }
    }, [id]);

    const handlePrint = useReactToPrint({
        contentRef,
    });

    if (loading)
        return (
            <Box display="flex" justifyContent="center" mt={4}>
                <CircularProgress />
            </Box>
        );

    if (!data) return <NotFound />;

    // 🧮 Calculate totals
    const openingBalance = Number(data.openingBalance || 0);
    const invoicedAmount = Number(data.invoicedAmount || 0);
    const amountPaid = Number(data.amountPaid || 0);
    const balanceDue = Number(data.balanceDue || 0);

    // 🧾 Compute running balance for transaction table
    let runningBalance = openingBalance;

    const formattedTransactions = data.transactions?.map((t: any) => {
        // Payment decreases, Invoice increases balance
        if (t.amount > 0 && t.payments === 0) {
            // Invoice
            runningBalance += Number(t.amount);
        } else if (t.payments > 0) {
            // Payment
            runningBalance -= Number(t.payments);
        }

        return {
            ...t,
            formattedDate: dateFormat(t.date, "DD/MM/YYYY"),
            balance: runningBalance,
        };
    });

    return (
        <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            p={4}
            bgcolor="grey.100"
            minHeight="100vh"
        >
            {/* Print Button */}
            <WSButton
                id="printBtn"
                size="small"
                label="Print Statement"
                onClick={handlePrint}
            />

            {/* Printable Content */}
            <Box
                ref={contentRef}
                mt={2}
                sx={{
                    p: 4,
                    width: "100%",
                    maxWidth: "850px",
                    bgcolor: "white",
                    // borderRadius: 2,
                    // boxShadow: 3,
                }}
            >
                {/* HEADER */}
                <Grid container justifyContent="space-between" alignItems="center">
                    <Grid item>
                        <Box display="flex" alignItems="center" gap={2}>
                            <img
                                src={logo}
                                alt="Company Logo"
                                width={110}
                                height="auto"
                                style={{ objectFit: "contain" }}
                            />
                        </Box>
                    </Grid>

                    <Grid item textAlign="right">
                        <Typography variant="h6" fontWeight={700} color="error">
                            {branchData?.name?.toUpperCase()}
                        </Typography>
                        <Typography variant="body2">
                            {branchData?.address?.addressLine1},{" "}
                            {branchData?.address?.addressLine2}
                        </Typography>
                        <Typography variant="body2">
                            P.O {branchData?.address?.pincode}, PH: {branchData?.phoneNumber}
                        </Typography>
                        <Typography variant="body2">
                            GSTIN: {branchData?.gstNumber || "—"}
                        </Typography>
                        <Typography variant="body2">{branchData?.email}</Typography>
                    </Grid>
                </Grid>

                <Divider sx={{ my: 2 }} />
                <Grid
                    container
                    justifyContent="space-between"
                    alignItems="flex-start"
                    spacing={2}
                    sx={{ mt: 2 }}
                >
                    {/* LEFT: Customer Info */}
                    <Grid item xs={6}>
                        <Box>
                            <Typography variant="subtitle2" fontWeight={700}>
                                To
                            </Typography>
                            <Typography fontSize="0.9rem" fontWeight={600}>
                                {data.customerName}
                            </Typography>
                            <Typography sx={{ fontSize: "0.875rem" }}>
                                GSTIN: {data.customerGSTIN || "—"}
                            </Typography>
                        </Box>
                    </Grid>

                    {/* RIGHT: Statement Period + Account Summary */}
                    <Grid item xs={6}>
                        <Box textAlign="right">
                            <Box display="inline-block" textAlign="right">
                                <Typography fontWeight={700} sx={{ display: "inline-block" }}>
                                    Statement of Accounts
                                </Typography>
                                <Divider
                                    sx={{
                                        my: 1,
                                        bgcolor: "black",
                                        height: "2px",
                                        width: "100%", // matches text width since parent is inline-block
                                    }}
                                />

                                <Typography sx={{ fontSize: "0.85rem" }}>
                                    {dateFormat(data.fromDate, "DD/MM/YYYY")} To{" "}
                                    {dateFormat(data.toDate, "DD/MM/YYYY")}
                                </Typography>

                                <Divider sx={{
                                    my: 1,
                                    bgcolor: "black",
                                    height: "2px",
                                    width: "100%",
                                }} />
                            </Box>

                            <Box mt={2}>
                                <Typography fontWeight={700} mb={1} textAlign={'left'} sx={{ padding: 1, bgcolor: 'lightgray' }}>
                                    Account Summary
                                </Typography>

                                <TableContainer component={Paper} sx={{ maxWidth: 400, ml: "auto" }}>
                                    <Table size="small" aria-label="account summary table">
                                        {/* <TableHead>
                                            <TableRow>
                                                <TableCell><strong>Description</strong></TableCell>
                                                <TableCell align="right"><strong>Amount</strong></TableCell>
                                            </TableRow>
                                        </TableHead> */}
                                        <TableBody>
                                            <TableRow>
                                                <TableCell>Opening Balance</TableCell>
                                                <TableCell align="right">{formatIndianCurrency(openingBalance)}</TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell>Invoiced Amount</TableCell>
                                                <TableCell align="right">{formatIndianCurrency(invoicedAmount)}</TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell>Amount Paid</TableCell>
                                                <TableCell align="right">{formatIndianCurrency(amountPaid)}</TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell>Balance Due</TableCell>
                                                <TableCell align="right">{formatIndianCurrency(balanceDue)}</TableCell>
                                            </TableRow>
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Box>
                        </Box>
                    </Grid>
                </Grid>

                <TableContainer
                    component={Paper}
                    sx={{
                        border: "1px solid #e0e0e0",
                        borderRadius: 1,
                        mt: 3
                    }}
                >
                    <Table size="small">
                        <TableHead sx={{ bgcolor: "#f9f9f9" }}>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>
                                    Transaction Details
                                </TableCell>
                                <TableCell align="right" sx={{ fontWeight: 700 }}>
                                    Invoice Amount
                                </TableCell>
                                <TableCell align="right" sx={{ fontWeight: 700 }}>
                                    Payments
                                </TableCell>
                                <TableCell align="right" sx={{ fontWeight: 700 }}>
                                    Balance
                                </TableCell>
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {/* Opening Balance */}
                            <TableRow>
                                <TableCell>
                                    {dateFormat(data.fromDate, "DD/MM/YYYY")}
                                </TableCell>
                                <TableCell>
                                    <strong>***Opening Balance***</strong>
                                </TableCell>
                                <TableCell align="right"> {formatIndianCurrency(openingBalance)}</TableCell>
                                <TableCell align="right">—</TableCell>
                                <TableCell align="right">
                                    {formatIndianCurrency(openingBalance)}
                                </TableCell>
                            </TableRow>

                            {/* Transactions */}
                            {formattedTransactions?.map((t: any, index: number) => (
                                <TableRow key={index}>
                                    <TableCell>{t.formattedDate}</TableCell>
                                    <TableCell>{t.details}</TableCell>
                                    <TableCell align="right">
                                        {t.amount > 0
                                            ? formatIndianCurrency(t.amount)
                                            : "—"}
                                    </TableCell>
                                    <TableCell align="right">
                                        {t.payments > 0
                                            ? formatIndianCurrency(t.payments)
                                            : "—"}
                                    </TableCell>
                                    <TableCell align="right">
                                        {formatIndianCurrency(t.balance)}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>

                <Divider sx={{ my: 3 }} />

                {/* BALANCE FOOTER */}
                <Typography textAlign="right" fontWeight={700}>
                    Balance Due {formatIndianCurrency(balanceDue)}
                </Typography>

                {/* <Divider sx={{ my: 3 }} /> */}

                {/* FOOTER CONTACT */}
                {/* <Box textAlign="center">
                    <Typography sx={{ fontSize: "0.85rem" }}>
                        {branchData?.name}
                    </Typography>
                    <Typography sx={{ fontSize: "0.85rem" }}>
                        {branchData?.address?.addressLine1},{" "}
                        {branchData?.address?.addressLine2},{" "}
                        {branchData?.address?.pincode}
                    </Typography>
                    <Typography sx={{ fontSize: "0.85rem" }}>
                        Email: {branchData?.email || "-"}
                    </Typography>
                </Box> */}
            </Box>
        </Box>
    );
};

export default CustomerStatementPrint;
